/**
 * Cart Actions
 */
/**
 * Internal dependencies
 */
import { cartApiService } from "../../services/api";
import type {
	RemoveFromCartCommand,
	UpdateCartItemCommand,
	UpdateEventDetailsCommand,
} from "../../services/api";
import type { Cart } from "../../types/cart.types";

import { ACTION_TYPES as types } from "./action-types";

// ── Synchronous action creators ───────────────────────────────────────────────

export function itemIsPendingDelete( itemId: number, isPending = true ) {
	return { type: types.ITEM_PENDING_DELETE, isPending, itemId } as const;
}

export function itemIsPendingQuantity( itemId: number, isPending = true ) {
	return { type: types.ITEM_PENDING_QUANTITY, isPending, itemId } as const;
}

export function removeCartItem( itemId: number ) {
	return { type: types.REMOVE_CART_ITEM, itemId } as const;
}

export function setCartData( cart: Cart ) {
	return { type: types.SET_CART_DATA, cart } as const;
}

export function setError( error: null | string ) {
	return { type: types.SET_ERROR, error } as const;
}

export function setLoading( isLoading: boolean ) {
	return { type: types.SET_LOADING, isLoading } as const;
}

export function updateCartItem(
	itemId: number,
	updates: Partial< {
		formatted_price: string;
		formatted_total: string;
		price: number;
		quantity: number;
		total: number;
	} >,
) {
	return { type: types.UPDATE_CART_ITEM, itemId, updates } as const;
}

// ── Internal dispatch shape ───────────────────────────────────────────────────
// Defined here so thunks and action creators share one file.
// `export * from './thunks'` is NOT used — the ESLint TypeScript language
// service fails to include `export *` re-exports when inferring literal keys
// for PromisifiedActionCreators. Inlining avoids that cross-file gap.

type Dispatch = {
	itemIsPendingDelete: ( itemId: number, isPending?: boolean ) => void;
	itemIsPendingQuantity: ( itemId: number, isPending?: boolean ) => void;
	removeCartItem: ( itemId: number ) => void;
	setCartData: ( cart: Cart ) => void;
	setError: ( error: null | string ) => void;
	setLoading: ( isLoading: boolean ) => void;
	updateCartItem: ( itemId: number, updates: Partial< { formatted_price: string; formatted_total: string; price: number; quantity: number; total: number } > ) => void;
	updateEventDetailsThunk: ( command: UpdateEventDetailsCommand ) => void;
};

// ── Thunks ────────────────────────────────────────────────────────────────────

export async function runFetchCart( { dispatch }: { dispatch: Dispatch } ) {
	try {
		dispatch.setLoading( true );
		dispatch.setError( null );
		const cart = await cartApiService.getCart();
		dispatch.setCartData( cart );
	} catch ( error ) {
		const message = error instanceof Error ? error.message : "Failed to fetch cart";
		dispatch.setError( message );
		dispatch.setCartData( {
			items: [],
			itemsCount: 0,
			subtotal: 0,
			total: 0,
			formattedSubtotal: "$0.00",
			formattedTotal: "$0.00",
			event_date: null,
			state: null,
			city: null,
		} );
	} finally {
		dispatch.setLoading( false );
	}
}

export const fetchCart = () => runFetchCart;

async function runRemoveCartItemThunk(
	command: RemoveFromCartCommand,
	{ dispatch }: { dispatch: Dispatch },
) {
	try {
		dispatch.itemIsPendingDelete( command.cart_item_id, true );
		dispatch.removeCartItem( command.cart_item_id );
		const cart = await cartApiService.removeItem( command );
		dispatch.setCartData( cart );
		dispatch.setError( null );
	} catch ( error ) {
		const message = error instanceof Error ? error.message : "Failed to remove item";
		dispatch.setError( message );
		await runFetchCart( { dispatch } );
	} finally {
		dispatch.itemIsPendingDelete( command.cart_item_id, false );
	}
}

export const removeCartItemThunk =
	( command: RemoveFromCartCommand ) =>
	( context: { dispatch: Dispatch } ) =>
		runRemoveCartItemThunk( command, context );

async function runUpdateCartItemQuantity(
	command: UpdateCartItemCommand,
	{ dispatch }: { dispatch: Dispatch },
) {
	try {
		dispatch.itemIsPendingQuantity( command.item_id, true );
		dispatch.updateCartItem( command.item_id, { quantity: command.quantity } );
		const cart = await cartApiService.updateItemQuantity( command );
		dispatch.setCartData( cart );
		dispatch.setError( null );
	} catch ( error ) {
		const message = error instanceof Error ? error.message : "Failed to update quantity";
		dispatch.setError( message );
		await runFetchCart( { dispatch } );
	} finally {
		dispatch.itemIsPendingQuantity( command.item_id, false );
	}
}

export const updateCartItemQuantity =
	( command: UpdateCartItemCommand ) =>
	( context: { dispatch: Dispatch } ) =>
		runUpdateCartItemQuantity( command, context );

async function runUpdateEventDetails(
	command: UpdateEventDetailsCommand,
	{ dispatch }: { dispatch: Dispatch },
) {
	try {
		const cart = await cartApiService.updateEventDetails( command );
		dispatch.setCartData( cart );
		dispatch.setError( null );
	} catch ( error ) {
		const message = error instanceof Error ? error.message : "Failed to update event details";
		dispatch.setError( message );
	}
}

export const updateEventDetailsThunk =
	( command: UpdateEventDetailsCommand ) =>
	( context: { dispatch: Dispatch } ) =>
		runUpdateEventDetails( command, context );
